/**
 * Email Service - Handles all email notifications
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Send Donation Confirmation Email
export const sendDonationConfirmationEmail = async (donationData, transactionData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/emails/donation-confirmation`, {
      donor_name: donationData.donorName,
      donor_email: donationData.donorEmail,
      amount: transactionData.amount,
      transaction_id: transactionData.transactionId,
      receipt_url: transactionData.receipt_url,
      donation_type: donationData.frequency,
      donation_purpose: donationData.purpose,
      timestamp: new Date().toISOString(),
    });

    return {
      success: true,
      messageId: response.data.message_id,
      sentAt: response.data.sent_at,
    };
  } catch (error) {
    console.error('Confirmation email error:', error);
    return {
      success: false,
      error: error.response?.data?.message || 'Failed to send confirmation email',
    };
  }
};

// Send Receipt Email
export const sendReceiptEmail = async (donorEmail, donorName, receiptData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/emails/receipt`, {
      donor_email: donorEmail,
      donor_name: donorName,
      receipt_url: receiptData.receipt_url,
      transaction_id: receiptData.transactionId,
      amount: receiptData.amount,
      processor: receiptData.processor,
      date: receiptData.date,
      tax_deductible: receiptData.taxDeductible,
    });

    return {
      success: true,
      messageId: response.data.message_id,
    };
  } catch (error) {
    console.error('Receipt email error:', error);
    return {
      success: false,
      error: 'Failed to send receipt',
    };
  }
};

// Send Welcome Email
export const sendWelcomeEmail = async (donorEmail, donorName) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/emails/welcome`, {
      donor_email: donorEmail,
      donor_name: donorName,
      timestamp: new Date().toISOString(),
    });

    return {
      success: true,
      messageId: response.data.message_id,
    };
  } catch (error) {
    console.error('Welcome email error:', error);
    return {
      success: false,
      error: 'Failed to send welcome email',
    };
  }
};

// Send Tax Information Email
export const sendTaxInformationEmail = async (donorEmail, donorName, taxData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/emails/tax-info`, {
      donor_email: donorEmail,
      donor_name: donorName,
      ein: taxData.ein,
      organization_name: taxData.organizationName,
      tax_status: taxData.taxStatus,
      timestamp: new Date().toISOString(),
    });

    return {
      success: true,
      messageId: response.data.message_id,
    };
  } catch (error) {
    console.error('Tax info email error:', error);
    return {
      success: false,
      error: 'Failed to send tax information',
    };
  }
};

// Send Recurring Donation Confirmation
export const sendRecurringDonationEmail = async (donorEmail, donorName, subscriptionData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/emails/recurring-confirmation`, {
      donor_email: donorEmail,
      donor_name: donorName,
      subscription_id: subscriptionData.subscriptionId,
      amount: subscriptionData.amount,
      frequency: subscriptionData.frequency,
      next_billing_date: subscriptionData.nextBillingDate,
      manage_url: `${window.location.origin}/donor/manage-donations`,
    });

    return {
      success: true,
      messageId: response.data.message_id,
    };
  } catch (error) {
    console.error('Recurring confirmation email error:', error);
    return {
      success: false,
      error: 'Failed to send recurring donation email',
    };
  }
};

// Send Newsletter Confirmation
export const sendNewsletterConfirmationEmail = async (email, name, preferences) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/emails/newsletter-confirmation`, {
      email,
      name,
      interests: preferences.interests,
      frequency: preferences.frequency,
      language: preferences.language,
    });

    return {
      success: true,
      messageId: response.data.message_id,
    };
  } catch (error) {
    console.error('Newsletter confirmation error:', error);
    return {
      success: false,
      error: 'Failed to send newsletter confirmation',
    };
  }
};

// Send Refund Notification
export const sendRefundNotificationEmail = async (donorEmail, donorName, refundData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/emails/refund-notification`, {
      donor_email: donorEmail,
      donor_name: donorName,
      original_transaction_id: refundData.originalTransactionId,
      refund_amount: refundData.refundAmount,
      refund_id: refundData.refundId,
      reason: refundData.reason,
      estimated_arrival: refundData.estimatedArrival,
    });

    return {
      success: true,
      messageId: response.data.message_id,
    };
  } catch (error) {
    console.error('Refund notification error:', error);
    return {
      success: false,
      error: 'Failed to send refund notification',
    };
  }
};

// Send Payment Failed Email
export const sendPaymentFailedEmail = async (donorEmail, donorName, failureData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/emails/payment-failed`, {
      donor_email: donorEmail,
      donor_name: donorName,
      error_message: failureData.errorMessage,
      retry_url: failureData.retryUrl,
      support_email: 'support@pathio.org',
    });

    return {
      success: true,
      messageId: response.data.message_id,
    };
  } catch (error) {
    console.error('Payment failed email error:', error);
    return {
      success: false,
      error: 'Failed to send payment failed notification',
    };
  }
};

// Send Impact Report Email
export const sendImpactReportEmail = async (donorEmail, donorName, reportData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/emails/impact-report`, {
      donor_email: donorEmail,
      donor_name: donorName,
      report_title: reportData.reportTitle,
      report_url: reportData.reportUrl,
      donation_amount: reportData.donationAmount,
      impact_description: reportData.impactDescription,
    });

    return {
      success: true,
      messageId: response.data.message_id,
    };
  } catch (error) {
    console.error('Impact report email error:', error);
    return {
      success: false,
      error: 'Failed to send impact report',
    };
  }
};

// Send Volunteer Opportunity Email
export const sendVolunteerOpportunityEmail = async (email, name, opportunityData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/emails/volunteer-opportunity`, {
      email,
      name,
      opportunity_title: opportunityData.title,
      opportunity_description: opportunityData.description,
      apply_url: opportunityData.applyUrl,
      deadline: opportunityData.deadline,
    });

    return {
      success: true,
      messageId: response.data.message_id,
    };
  } catch (error) {
    console.error('Volunteer opportunity email error:', error);
    return {
      success: false,
      error: 'Failed to send volunteer opportunity',
    };
  }
};

// Send Partnership Email
export const sendPartnershipInquiryEmail = async (email, name, organizationData) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/emails/partnership-inquiry`, {
      email,
      name,
      organization_name: organizationData.organizationName,
      organization_type: organizationData.organizationType,
      inquiry_message: organizationData.inquiryMessage,
      contact_number: organizationData.contactNumber,
    });

    return {
      success: true,
      messageId: response.data.message_id,
    };
  } catch (error) {
    console.error('Partnership inquiry email error:', error);
    return {
      success: false,
      error: 'Failed to send partnership inquiry',
    };
  }
};
// Send Bulk Email (Newsletter)
export const sendBulkNewsletterEmail = async (recipients, newsletter) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/emails/bulk-newsletter`, {
      recipients, // Array of {email, name}
      subject: newsletter.subject,
      content: newsletter.content,
      html_content: newsletter.htmlContent,
      unsubscribe_link: `${window.location.origin}/unsubscribe`,
    });

    return {
      success: true,
      messageId: response.data.message_id,
      sentCount: response.data.sent_count,
    };
  } catch (error) {
    console.error('Bulk newsletter error:', error);
    return {
      success: false,
      error: 'Failed to send bulk newsletter',
    };
  }
};

// Unsubscribe from Newsletter
export const unsubscribeFromNewsletter = async (email) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/emails/unsubscribe`, {
      email,
    });

    return {
      success: true,
      unsubscribedAt: response.data.unsubscribed_at,
    };
  } catch (error) {
    console.error('Unsubscribe error:', error);
    return {
      success: false,
      error: 'Failed to unsubscribe',
    };
  }
};

// Update Email Preferences
export const updateEmailPreferences = async (email, preferences) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/emails/preferences`, {
      email,
      language: preferences.language,
      frequency: preferences.frequency,
      interests: preferences.interests,
      marketing_emails: preferences.marketingEmails,
      impact_updates: preferences.impactUpdates,
      event_invitations: preferences.eventInvitations,
    });

    return {
      success: true,
      updatedAt: response.data.updated_at,
    };
  } catch (error) {
    console.error('Update preferences error:', error);
    return {
      success: false,
      error: 'Failed to update email preferences',
    };
  }
};

const emailServiceExports = {
  sendDonationConfirmationEmail,
  sendReceiptEmail,
  sendWelcomeEmail,
  sendTaxInformationEmail,
  sendRecurringDonationEmail,
  sendNewsletterConfirmationEmail,
  sendRefundNotificationEmail,
  sendPaymentFailedEmail,
  sendImpactReportEmail,
  sendVolunteerOpportunityEmail,
  sendPartnershipInquiryEmail,
  sendBulkNewsletterEmail,
  unsubscribeFromNewsletter,
  updateEmailPreferences,
};

export default emailServiceExports;
